import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { FileTextIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { BillFileDrawer } from "@/components/BillFileDrawer";
import { BillLlmExtractionCard } from "@/components/BillLlmExtractionCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatCurrency, formatDateTime } from "@/lib/format";

type InboxBill = components["schemas"]["CompanyInboxBillAdminResponse"];
type Candidate = components["schemas"]["CompanyInboxBillCandidateResponse"];
type InboxStatus = components["schemas"]["CompanyInboxBillStatus"];

// A company-inbox document not yet a Bill. It keeps its id once routed, so a 404 here usually means it was
// routed: check the Bills table and forward.
export function CompanyInboxBillPage() {
  const { billId } = useParams<{ billId: string }>();

  const [bill, setBill] = useState<InboxBill | null>(null);
  const [routed, setRouted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileDrawerOpen, setFileDrawerOpen] = useState(false);

  useEffect(() => {
    if (!billId) return;
    let cancelled = false;

    async function fetchBill() {
      setLoading(true);
      setError(null);
      setRouted(false);
      const { data, error: apiError, response } = await api.GET("/admin/company-inbox-bills/{billId}", {
        params: { path: { billId: billId! } },
      });
      if (cancelled) return;

      if (!apiError) {
        setBill(data ?? null);
      } else if (response?.status === 404) {
        const promoted = await api.GET("/admin/bills/{id}", { params: { path: { id: billId! } } });
        if (cancelled) return;
        if (promoted.data) setRouted(true);
        else setBill(null);
      } else {
        setError("Failed to load company inbox bill.");
      }
      setLoading(false);
    }

    fetchBill();
    return () => {
      cancelled = true;
    };
  }, [billId]);

  if (routed && billId) return <Navigate to={`/bills/${billId}`} replace />;

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const currency = bill?.currency ?? "GBP";

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Company inbox bill</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Received against a company and not yet routed to an organisation.
          </p>
        </div>
        {bill && (
          <Button variant="outline" onClick={() => setFileDrawerOpen(true)} disabled={!bill.fileName}>
            <FileTextIcon className="size-4" />
            View file
          </Button>
        )}
      </div>

      {bill?.id && (
        <BillFileDrawer
          billId={bill.id}
          fileName={bill.fileName}
          open={fileDrawerOpen}
          onOpenChange={setFileDrawerOpen}
          source="companyInboxBill"
        />
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : bill ? (
        <>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>
                {bill.invoiceNumber || <span className="text-muted-foreground">Unnumbered</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Section title="Inbox">
                <Field label="Bill ID">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {bill.id}
                  </Badge>
                </Field>
                <Field label="Status">
                  <Badge variant={statusVariant(bill.status)}>{bill.status}</Badge>
                </Field>
                <Field label="Source">{bill.source ?? "—"}</Field>
                <Field label="Client">
                  <IdLink to={`/clients/${bill.clientId}`} id={bill.clientId} />
                </Field>
                <Field label="Company">
                  <IdLink to={`/companies/${bill.companyId}`} id={bill.companyId} />
                </Field>
                <Field label="File name">{bill.fileName ?? "—"}</Field>
              </Section>

              <Section title="Routing">
                <Field label="Outcome">
                  {bill.routingOutcome ? (
                    <Badge variant="secondary">{bill.routingOutcome}</Badge>
                  ) : (
                    <span className="text-muted-foreground">Not routed yet</span>
                  )}
                </Field>
                <Field label="Routed at">{formatDateTime(bill.routedAt)}</Field>
                <Field label="Suggested organisation">
                  <IdLink
                    to={`/organisations/${bill.suggestedOrganisationId}`}
                    id={bill.suggestedOrganisationId}
                  />
                  {bill.suggestedOrganisationConfidence != null && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      {formatScore(bill.suggestedOrganisationConfidence)}
                    </span>
                  )}
                </Field>
                <Field label="Delivery address" className="sm:col-span-3">
                  {bill.deliveryAddress ?? "—"}
                </Field>
              </Section>

              <Section title="Supplier">
                <Field label="Name">{bill.supplierName ?? "—"}</Field>
                <Field label="Email">{bill.supplierEmail ?? "—"}</Field>
                <Field label="Address">{bill.supplierAddress ?? "—"}</Field>
              </Section>

              <Section title="Document">
                <Field label="Invoice date">{formatDateTime(bill.invoiceDate)}</Field>
                <Field label="Due date">{formatDateTime(bill.dueDate)}</Field>
                <Field label="PO numbers">
                  {bill.billPoNumbers?.length ? bill.billPoNumbers.join(", ") : "—"}
                </Field>
                <Field label="Subtotal">{formatCurrency(bill.subTotal, currency)}</Field>
                <Field label="Tax">{formatCurrency(bill.taxAmount, currency)}</Field>
                <Field label="Total">
                  <span className="font-semibold">{formatCurrency(bill.totalAmount, currency)}</span>
                </Field>
              </Section>

              <Section title="Extraction">
                <Field label="Confidence">
                  {bill.confidence != null ? `${Math.round(Number(bill.confidence) * 100)}%` : "—"}
                </Field>
                <Field label="Retries">{bill.extractionRetryCount ?? 0}</Field>
                {bill.extractionError && (
                  <Field label="Error" className="sm:col-span-3">
                    <span className="text-destructive">{bill.extractionError}</span>
                  </Field>
                )}
              </Section>

              {bill.discardedAt && (
                <Section title="Discarded">
                  <Field label="Discarded at">{formatDateTime(bill.discardedAt)}</Field>
                  <Field label="Discarded by">
                    <IdLink to={`/users/${bill.discardedBy}`} id={bill.discardedBy} />
                  </Field>
                  <Field label="Reason">{bill.discardReason ?? "—"}</Field>
                </Section>
              )}

              <Section title="Audit">
                <Field label="Created">{formatDateTime(bill.createdAt)}</Field>
                <Field label="Created by">
                  <IdLink to={`/users/${bill.createdBy}`} id={bill.createdBy} />
                </Field>
                <Field label="Updated">{formatDateTime(bill.updatedAt)}</Field>
              </Section>
            </CardContent>
          </Card>

          <BillLlmExtractionCard billId={bill.id!} />

          <h2 className="mb-4 mt-8 text-lg font-semibold">Candidate organisations</h2>
          <CandidatesTable candidates={bill.candidates ?? []} />
        </>
      ) : (
        <p className="text-muted-foreground">Company inbox bill not found.</p>
      )}
    </div>
  );
}

function CandidatesTable({ candidates }: { candidates: Candidate[] }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organisation</TableHead>
            <TableHead>Basis</TableHead>
            <TableHead className="text-right">Score</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {candidates.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground h-24 text-center">
                No candidate organisations.
              </TableCell>
            </TableRow>
          ) : (
            candidates.map((c) => (
              <TableRow key={c.organisationId}>
                <TableCell>
                  <IdLink to={`/organisations/${c.organisationId}`} id={c.organisationId} />
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{c.basis}</Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatScore(c.score)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function statusVariant(status: InboxStatus | undefined): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "AwaitingOrganisation":
      return "default";
    case "ExtractionFailed":
      return "destructive";
    case "Discarded":
      return "secondary";
    default:
      return "outline";
  }
}

function formatScore(score: number | string | null | undefined): string {
  return score == null ? "—" : `${Math.round(Number(score) * 100)}%`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function IdLink({ to, id }: { to: string; id?: string | null }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={to}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {id.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
